<ng-container *ngIf="serverError">
  <div class="modal-header">
    <h4 class="modal-title">Server Error: {{ serverError }}</h4>
    <button type="button" class="close" aria-label="Close" (click)="activeModal.dismiss()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
</ng-container>

<ng-container *ngIf="!serverError">

  <div class="modal-header">
    <h4 class="modal-title" *ngIf="!isResend()">Invite Waitlist Entry To Roster</h4>
    <h4 class="modal-title" *ngIf="isResend()">Resend Invitation</h4>
    <button type="button" class="close" aria-label="Close" (click)="activeModal.dismiss()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">

    <div class="alert alert-danger text-center mb-3" role="alert"
         *ngIf="errorMessage">
      {{ errorMessage }}
    </div>

    <p *ngIf="isResend()">
      This will send <b>{{ waitlistEntry.first_name }} {{ waitlistEntry.last_name }}</b>
      the same invitation again, to join the
      <b>{{ waitlistEntry.roster ? waitlistEntry.roster.name : '' }}</b> roster of
      <b>{{ waitlistEntry.event_type_name }}</b>. Their entry stays pending.
    </p>

    <div class="mb-3">
      <label for="selectedRoster">Roster</label>
      <select *ngIf="loadingRosters"
              class="custom-select disabled" disabled>
        <option>Loading...</option>
      </select>
      <select *ngIf="!loadingRosters"
              class="custom-select"
              id="selectedRoster" name="selectedRoster"
              [(ngModel)]="selectedRoster">
        <option disabled class="disabled"
                *ngIf="selectedRoster === null">Select a Roster</option>
        <option *ngFor="let roster of rosters"
                [ngValue]="roster">
          {{ roster.name }}
        </option>
      </select>
    </div>

    <div class="mb-3">
      <a href="#" class="float-right"
         *ngIf="startDate"
         (click)="$event.preventDefault(); handleClearStartDate();">
        Clear
      </a>
      <label for="startDate">Start Date (optional)</label>
      <app-date-picker [id]="'startDate'"
                       [(content)]="startDate"></app-date-picker>
      <small class="form-text text-muted">
        If this is left blank, the customer chooses a start date when they purchase, if the
        membership allows that. Otherwise the roster membership starts on the day it is purchased.
      </small>
      <app-roster-invite-first-bill [rosterId]="selectedRoster ? selectedRoster.id : null"
                                    [startDate]="startDate"></app-roster-invite-first-bill>
    </div>

    <div class="mb-3">
      <a href="#" class="float-right"
         *ngIf="newDeadlineDate || newDeadlineTime"
         (click)="$event.preventDefault(); handleClearNewDeadline();">
        Clear
      </a>
      <label class="form-text" for="newDeadlineDate">
        {{ isResend() ? 'New Booking Deadline (optional)' : 'Booking Deadline (optional)' }}
      </label>
      <app-deadline-date-time-input [idPrefix]="'newDeadline'"
                                    [timeZone]="deadlineTimeZone()"
                                    [(date)]="newDeadlineDate"
                                    [(time)]="newDeadlineTime"></app-deadline-date-time-input>
      <small class="form-text text-muted"
             *ngIf="isResend() && waitlistEntry.booking_deadline">
        Leave this blank to keep the current deadline of
        {{ dateStringFromISO8601(waitlistEntry.booking_deadline, waitlistEntry.time_zone) }},
        {{ timeStringFromISO8601(waitlistEntry.booking_deadline, waitlistEntry.time_zone) }}.
      </small>
      <small class="form-text text-muted"
             *ngIf="isResend() && !waitlistEntry.booking_deadline">
        This entry currently has no deadline. Leave this blank to keep it that way.
      </small>
      <small class="form-text text-muted"
             *ngIf="!isResend()">
        Leave this blank to use the event type's default acceptance window. If the event type has none, the invitation will not expire.
      </small>
      <small class="form-text text-danger"
             *ngIf="newDeadlineIncomplete()">
        Enter both a date and a time for the new deadline.
      </small>
    </div>

    <div class="form-group">
      <label>Invite Note (optional)</label>
      <app-markdown-editor [id]="'inviteNote'"
                           [(content)]="inviteNote"
                           [shortEditor]="true"></app-markdown-editor>
      <small class="form-text text-muted">
        This text will be included in the invitation, underneath information about
        the event and (if set above) the booking deadline. It is prefilled with the
        note from this entry's last invitation, if there was one; clearing it sends
        the invitation with no note.
      </small>
    </div>

    <div class="form-group">
      <div class="form-check"
           [ngbTooltip]="participantBelowMinAge ? 'Required because this participant is below the minimum age to own a membership' : null"
           container="body">
        <input class="form-check-input" type="checkbox"
               id="sendInviteToParentsAndGuardians"
               name="sendInviteToParentsAndGuardians"
               [disabled]="loadingSubmit || participantBelowMinAge"
               [class.disabled]="loadingSubmit || participantBelowMinAge"
               [(ngModel)]="sendInviteToParentsAndGuardians">
        <label class="form-check-label"
               for="sendInviteToParentsAndGuardians">
          Send invite to linked parents &amp; guardians
        </label>
      </div>
      <small class="form-text text-muted">
        If selected, the invite will be sent to any linked parents and guardians of the participant to accept or reject the invite on behalf of the participant. This is in addition to the participant's own email address.
      </small>
    </div>

    <div class="alert alert-danger" role="alert"
         *ngIf="hasNoEmailRecipient() && !loadingRelations">
      <b>{{ waitlistEntry.first_name }} {{ waitlistEntry.last_name }}</b> has no
      email address on file, so <b>no email will actually be sent</b>. The
      invitation will still be recorded, and they can still be booked in by
      staff, but they will not be notified.
    </div>

    <p *ngIf="emailRecipients().length > 0">
      This will send an email to
      <b *ngFor="let recipient of emailRecipients(); let last = last">{{ recipient }}{{ last ? '' : ', ' }}</b>
      inviting them to book the <b>{{ waitlistEntry.event_type_name }}</b> for <b>{{ waitlistEntry.first_name }} {{ waitlistEntry.last_name }}.</b>
    </p>

  </div>

  <div class="modal-footer">
    <button type="button" class="btn btn-secondary"
            (click)="activeModal.close()">
      Cancel
    </button>
    <button class="btn btn-primary" type="button"
            *ngIf="!loadingSubmit"
            [disabled]="submitDisabled()"
            [class.disabled]="submitDisabled()"
            (click)="handleSendInvite()">
      {{ isResend() ? 'Resend Invitation' : 'Send Invite' }}
    </button>
    <button type="button" class="btn btn-primary disabled" disabled
            *ngIf="loadingSubmit">
      Loading...
    </button>
  </div>

</ng-container>
